import React, { useCallback, useMemo, useState } from 'react';

import { theme } from '~/theme';

import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { green } from '@mui/material/colors';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import useToggleState from '~/hooks/useToggleState';

import { MergedRate, Rate } from '~/services/AccommodationService';

import { pluralizeToString } from '~/utils/stringUtils';

import OriginalPrice from '../OriginalPrice';
import RateDetailsModal from '../RateDetailsModal';
import RateTypeBadge from '../RateTypeBadge';
import { formatMargin, formatPrice } from '../utils/formatPrice';
import { describePriceMatch, formatMarginChange, getPriceMatchDetails } from '../utils/priceMatch';

interface GroupedRatesProps {
  winningRate: Rate;
  mergedRates: Array<MergedRate>;
  currency: string;
}

type MergedRateDebug = MergedRate & {
  winningReservationRoomRateId?: string;
  losingReservationRoomRateId?: string;
  winningSellingPrice?: number;
  losingSellingPrice?: number;
  priceDifference?: number;
  thresholdAmount?: number;
  thresholdPercent?: number;
  thresholdPolicyDescription?: string;
};

const MERGE_REASON_LABELS: Record<string, string> = {
  'dynamic-price-match': 'Lost to dynamic price match',
};

const formatDebugRateId = (id: string | undefined) => {
  return id ? id.slice(0, 8) : 'unknown';
};

const getRateName = (rate: Rate) => {
  if (rate.meta.type === 'reservation') {
    return rate.meta.name;
  }
  return rate.name || rate.meta.supplier || 'Bedbank rate';
};

const getReservationRoomRateId = (rate: Rate) => {
  return rate.meta.type === 'reservation' ? rate.meta.reservationRoomRateId : rate.id;
};

export default function GroupedRates({ winningRate, mergedRates, currency }: GroupedRatesProps) {
  const { isToggled: isOpen, toggleOn: openModal, toggleOff: closeModal } = useToggleState(false);
  const {
    isToggled: isRateDetailsModalOpen,
    toggleOn: openRateDetailsModal,
    toggleOff: closeRateDetailsModal,
  } = useToggleState(false);
  const [selectedRate, setSelectedRate] = useState<Rate | null>(null);

  const rows = useMemo(() => {
    return [winningRate].concat(
      mergedRates.map((mr) => ({
        ...(mr.rate as Rate),
        description: mr.description,
        mergeReason: mr.mergeReason,
      })),
    );
  }, [winningRate, mergedRates]);

  const handleDetailsClick = useCallback(
    (rate: Rate) => {
      setSelectedRate(rate);
      openRateDetailsModal();
    },
    [openRateDetailsModal],
  );

  if (mergedRates.length === 0) {
    return null;
  }

  const renderMergeSummary = (mergedRate: MergedRateDebug) => {
    const losingRate = mergedRate.rate as Rate;
    const winningPrice = mergedRate.winningSellingPrice ?? winningRate.total.sellingPrice;
    const losingPrice = mergedRate.losingSellingPrice ?? losingRate.total.sellingPrice;
    const priceDifference = mergedRate.priceDifference ?? losingPrice - winningPrice;
    const thresholdParts = [
      mergedRate.thresholdAmount !== undefined ? formatPrice(mergedRate.thresholdAmount, currency) : undefined,
      mergedRate.thresholdPercent !== undefined ? `${mergedRate.thresholdPercent}%` : undefined,
    ]
      .filter(Boolean)
      .join(' / ');
    const allowedCap = mergedRate.thresholdPolicyDescription ?? (thresholdParts || 'N/A');
    const winningRateId = mergedRate.winningReservationRoomRateId ?? getReservationRoomRateId(winningRate);
    const losingRateId = mergedRate.losingReservationRoomRateId ?? getReservationRoomRateId(losingRate);

    return (
      <Box key={losingRateId} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 2, mb: 2 }}>
        <Stack spacing={0.75}>
          <Typography variant="subtitle2">Merged rate explanation</Typography>
          <Typography variant="body2">
            Winner: {formatDebugRateId(winningRateId)} {getRateName(winningRate)} {formatPrice(winningPrice, currency)}
          </Typography>
          <Typography variant="body2">
            Loser: {formatDebugRateId(losingRateId)} {getRateName(losingRate)} {formatPrice(losingPrice, currency)}
          </Typography>
          <Typography variant="body2">Gap: {formatPrice(priceDifference, currency)}</Typography>
          <Typography variant="body2">Allowed cap: {allowedCap}</Typography>
          <Typography variant="body2">Outcome: {mergedRate.description}</Typography>
        </Stack>
      </Box>
    );
  };

  const columns: Array<GridColDef> = [
    {
      field: 'rateType',
      headerName: 'Rate Type',
      flex: 1,
      renderCell: (params) => (
        <Box>
          <RateTypeBadge rate={params.row} />
        </Box>
      ),
    },
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'basePrice',
      headerName: 'Base Price',
      flex: 1,
      renderCell: (params) => formatPrice(params.row.total?.beforeTaxesFeesPrice as number, currency),
    },
    {
      field: 'taxesAndFees',
      headerName: 'Taxes & Fees',
      flex: 1,
      renderCell: (params) => formatPrice(params.row.total?.taxesAndFees as number, currency),
    },
    {
      field: 'propertyFees',
      headerName: 'Payable at Property',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => {
        const hasPropertyFees = params.row.total?.propertyFees > 0;
        return hasPropertyFees ? formatPrice(params.row.total?.propertyFees as number, currency) : 'None';
      },
    },
    {
      field: 'totalPrice',
      headerName: 'Total Price',
      flex: 1,
      renderCell: (params) => {
        const displayPrice = (params.row.total?.sellingPrice || 0) + (params.row.total?.propertyFees || 0);
        const priceMatch = getPriceMatchDetails(params.row, { displayPrice });
        return (
          <Stack justifyContent="center">
            <Box>{formatPrice(displayPrice, currency)}</Box>
            {priceMatch?.originalDisplayPrice != null && (
              <OriginalPrice price={priceMatch.originalDisplayPrice} currency={currency} variant="stacked" />
            )}
          </Stack>
        );
      },
    },
    {
      field: 'luxplusPrice',
      headerName: 'Luxplus Price',
      flex: 1,
      renderCell: (params) => {
        // A withdrawn member price has no totals left to render — the match deleted them — so the
        // adjuster's record of what it had been is the only thing standing in for it.
        const { withdrawn, originalDisplayPrice } = getPriceMatchDetails(params.row)?.luxPlus ?? {
          withdrawn: false,
        };
        return (
          <Stack justifyContent="center">
            <Box sx={withdrawn ? { color: 'text.secondary', fontStyle: 'italic' } : undefined}>
              {withdrawn ? 'Withdrawn' : formatPrice(params.row.luxPlus?.displayPrice || 0, currency)}
            </Box>
            {originalDisplayPrice != null && (
              <OriginalPrice price={originalDisplayPrice} currency={currency} variant="stacked" />
            )}
          </Stack>
        );
      },
    },
    {
      field: 'savings',
      headerName: 'Savings',
      // flex: 1,
      renderCell: (params) => {
        return params.row.total?.discount > 0 ? (
          <Chip label={`${(params.row.total.discount * 100).toFixed(0)}% off`} color="secondary" size="small" />
        ) : (
          'No discount'
        );
      },
    },
    {
      field: 'margin',
      headerName: 'Margin',
      // flex: 1,
      renderCell: (params) => {
        // The dollar figure under the percent: a price match is decided on margin earned, and two
        // rates at the same price can sit at very different percentages of very different bases.
        // `marginAud` is always AUD, whatever currency the search ran in, so it is formatted as
        // AUD rather than with `currency`.
        const marginAud = params.row.total?.marginAud;
        // On a matched rate the pair is the point: it gave up margin to reach the group's
        // cheapest price, and what is left is what has to beat the rate it displaced.
        const marginChange = getPriceMatchDetails(params.row)?.marginChange;
        if (marginChange) {
          const formatted = formatMarginChange(marginChange);
          return (
            <Stack justifyContent="center">
              {formatted.percent != null && <Box>{formatted.percent}</Box>}
              <Typography variant="caption" color="text.secondary">
                {formatted.dollars}
              </Typography>
            </Stack>
          );
        }
        return (
          <Stack justifyContent="center">
            <Box>{formatMargin(params.row.total.margin)}</Box>
            {marginAud != null && (
              <Typography variant="caption" color="text.secondary">
                {formatPrice(marginAud, 'AUD')}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'mergeReason',
      headerName: 'Reason',
      flex: 3,
      renderCell: (params) => {
        if (params.row.mergeReason) {
          return (
            <Tooltip title={params.row.description}>
              <Box>{MERGE_REASON_LABELS[params.row.mergeReason] ?? params.row.mergeReason}</Box>
            </Tooltip>
          );
        }

        // The winner may have won only because its price was matched down to the group's cheapest
        // out of its own margin — worth saying, since its displayed price no longer explains why
        // it beat the others.
        const priceMatch = getPriceMatchDetails(params.row);
        if (priceMatch) {
          return (
            <Tooltip title={describePriceMatch(priceMatch, currency, { basis: 'selling' })}>
              <Chip size="small" color="warning" label="Winning Rate — dynamic price match" />
            </Tooltip>
          );
        }

        return <Box>Winning Rate</Box>;
      },
    },
    {
      field: 'details',
      headerName: 'Details',
      flex: 1,
      renderCell: (params) => (
        <IconButton size="small" color="primary" onClick={() => handleDetailsClick(params.row)}>
          <InfoIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Chip label={pluralizeToString('rate', mergedRates.length)} size="small" color="secondary" onClick={openModal} />
      <Dialog
        open={isOpen}
        onClose={closeModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            position: 'relative',
            overflow: 'visible',
          },
        }}
      >
        <DialogTitle>
          <Stack>{winningRate.groupKey}</Stack>
          <IconButton
            aria-label="close"
            onClick={closeModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {mergedRates.map((mergedRate) => renderMergeSummary(mergedRate as MergedRateDebug))}
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 5 },
              },
            }}
            pageSizeOptions={[5, 10, 20]}
            disableColumnFilter
            disableColumnMenu
            disableDensitySelector
            disableColumnSelector
            hideFooterSelectedRowCount
            getRowClassName={(params) => (params.row.id === rows[0].id ? 'Mui-br' : 'Mui-nonbr')}
            // Rows size to their content rather than to a fixed height: the margin cell stacks a
            // percentage over a dollar figure, and a fixed row clips the second line. Also lets
            // the wordier merge reasons wrap instead of being truncated.
            getRowHeight={() => 'auto'}
            sx={{
              '.Mui-br': {
                bgcolor: green[100],
              },
              // An auto-height row has no vertical padding of its own, so cells would otherwise
              // sit flush against the row divider.
              '& .MuiDataGrid-cell': {
                py: 1,
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Close</Button>
        </DialogActions>
      </Dialog>
      {isRateDetailsModalOpen && (
        <Dialog open={isRateDetailsModalOpen} onClose={closeRateDetailsModal} maxWidth="md" fullWidth>
          <DialogTitle>
            Rate Details
            <IconButton
              aria-label="close"
              onClick={closeRateDetailsModal}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <RateDetailsModal rate={selectedRate} currency={currency} mergedRates={mergedRates} />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeRateDetailsModal}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
